import { makeAutoObservable, runInAction } from 'mobx';
import {
  getOperatorsListRequest,
  createOperatorRequest,
  updateOperatorRequest,
  getOperatorRolesRequest
} from 'services/requestAgent';
import { toast } from 'react-toastify';
import i18n from '../i18n';

class OperatorsStore {
  isInitialized = false;
  isLoading = false;
  operatorsList = [];
  availableRoles = [];
  error = null;

  constructor() {
    makeAutoObservable(this);
  }

  resetOperatorsStore = () => {
    this.isInitialized = false;
    this.isLoading = false;
    this.operatorsList = [];
    this.availableRoles = [];
    this.error = null;
  };

  setIsLoading = (status) => {
    this.isLoading = status;
    this.error = null;
  };

  getOperatorsList = async () => {
    this.setIsLoading(true);

    try {
      const res = await getOperatorsListRequest();

      runInAction(() => {
        this.isInitialized = true;
        this.operatorsList = res || [];
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        toast.error(i18n.getMessage('operators.error.loadFailed'));
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  getAvailableRoles = async () => {
    try {
      const res = await getOperatorRolesRequest();

      runInAction(() => {
        this.availableRoles = res || [];
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    }
  };

  createOperator = async (data) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(createOperatorRequest(data), {
      pending: i18n.getMessage('operators.createOperator.pending'),
      success: i18n.getMessage('operators.createOperator.success')
    });

    try {
      const newOperator = await toastPromise;

      runInAction(() => {
        this.operatorsList = [newOperator, ...this.operatorsList];
      });

      return newOperator;
    } catch (err) {
      runInAction(() => {
        this.error = err;
        toast.error(i18n.getMessage('operators.createOperator.error'));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateOperator = async (data) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(updateOperatorRequest(data), {
      pending: i18n.getMessage('operators.updateOperator.pending'),
      success: i18n.getMessage('operators.updateOperator.success')
    });

    try {
      const updatedOperator = await toastPromise;

      runInAction(() => {
        const index = this.operatorsList.findIndex((op) => op.id === updatedOperator.id);
        if (index !== -1) {
          this.operatorsList[index] = updatedOperator;
        }
      });

      return updatedOperator;
    } catch (err) {
      runInAction(() => {
        this.error = err;
        toast.error(i18n.getMessage('operators.updateOperator.error'));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };
}

export default new OperatorsStore();
